import { useCallback, useEffect, useRef, useState } from 'react';

import {
  clearStoredDashboardEditLockHolder,
  getStoredDashboardEditLockHolder,
  setStoredDashboardEditLockHolder
} from './storage';
import { getTabId } from './tabId';
import { EDIT_LOCK_CHANNEL_NAME, type EditLockMessage } from './types';

/**
 * Manages a cross-tab edit session lock for the dashboard.
 *
 * When a user tries to enter Edit mode while another browser tab already holds
 * the edit lock, a conflict dialog is shown. The user can either take over the
 * session (forcing the other tab out of Edit mode) or keep the other session
 * active (cancel the new one).
 *
 * Uses BroadcastChannel exclusively for cross-tab signalling and state sync.
 * A QUERY_LOCK_STATE message on mount discovers any lock already held by another tab.
 */
export function useDashboardEditLock() {
  const tabId = useRef(getTabId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const pendingActionRef = useRef<{
    onProceed: () => void;
    claimEditLock: boolean;
  } | null>(null);
  const forceReleaseCallbackRef = useRef<(() => void) | null>(null);
  /** Tracks which tab currently holds the edit lock (null = nobody). */
  const holderTabIdRef = useRef<string | null>(null);

  const isOtherTabEditing = useCallback((): boolean => {
    const storedHolder = getStoredDashboardEditLockHolder();
    if (storedHolder) {
      holderTabIdRef.current = storedHolder;
    } else if (holderTabIdRef.current !== tabId.current) {
      holderTabIdRef.current = null;
    }

    const holder = holderTabIdRef.current;
    return holder !== null && holder !== tabId.current;
  }, []);

  const claimLock = useCallback(() => {
    setStoredDashboardEditLockHolder(tabId.current);
    holderTabIdRef.current = tabId.current;
    channelRef.current?.postMessage({
      type: 'CLAIMED',
      tabId: tabId.current
    } satisfies EditLockMessage);
  }, []);

  const releaseLock = useCallback(() => {
    if (holderTabIdRef.current !== tabId.current) return;
    holderTabIdRef.current = null;
    clearStoredDashboardEditLockHolder(tabId.current);
    channelRef.current?.postMessage({
      type: 'RELEASED',
      tabId: tabId.current
    } satisfies EditLockMessage);
  }, []);

  /**
   * Call when the user requests to enter Edit mode.
   * If another tab already holds the edit lock, shows a conflict dialog.
   * Otherwise claims the lock and calls onProceed immediately.
   */
  const requestEdit = useCallback(
    (onProceed: () => void) => {
      if (isOtherTabEditing()) {
        pendingActionRef.current = { onProceed, claimEditLock: true };
        setIsConflictDialogOpen(true);
      } else {
        claimLock();
        onProceed();
      }
    },
    [isOtherTabEditing, claimLock]
  );

  /**
   * Guards an action that must not run while another tab is editing, without
   * making this tab the edit lock holder.
   */
  const requestExclusiveAction = useCallback(
    (onProceed: () => void) => {
      if (isOtherTabEditing()) {
        pendingActionRef.current = { onProceed, claimEditLock: false };
        setIsConflictDialogOpen(true);
      } else {
        onProceed();
      }
    },
    [isOtherTabEditing]
  );

  /**
   * Register a callback that will be invoked when another tab sends FORCE_RELEASE,
   * i.e. when the current tab should exit Edit mode because another tab took over.
   */
  const setForceReleaseCallback = useCallback((cb: () => void) => {
    forceReleaseCallbackRef.current = cb;
  }, []);

  /** Conflict dialog: user chooses to end the other session and continue here. */
  const handleEndOtherSession = useCallback(() => {
    channelRef.current?.postMessage({ type: 'FORCE_RELEASE' } satisfies EditLockMessage);
    const pendingAction = pendingActionRef.current;
    if (pendingAction?.claimEditLock) claimLock();
    setIsConflictDialogOpen(false);
    pendingAction?.onProceed();
    pendingActionRef.current = null;
  }, [claimLock]);

  /** Conflict dialog: user chooses to keep the other session active (cancel here). */
  const handleCancelEdit = useCallback(() => {
    setIsConflictDialogOpen(false);
    pendingActionRef.current = null;
  }, []);

  useEffect(() => {
    // Initialise from storage immediately so isOtherTabEditing() is correct
    // before the async LOCK_STATE_RESPONSE can arrive (guards against remount races).
    const stored = getStoredDashboardEditLockHolder();
    if (stored && stored !== tabId.current) {
      holderTabIdRef.current = stored;
    }

    const channel = new BroadcastChannel(EDIT_LOCK_CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<EditLockMessage>) => {
      const { data } = event;
      switch (data.type) {
        case 'CLAIMED':
          if (data.tabId !== tabId.current) {
            if (holderTabIdRef.current === tabId.current) {
              clearStoredDashboardEditLockHolder(tabId.current);
              forceReleaseCallbackRef.current?.();
            }
            holderTabIdRef.current = data.tabId;
          }
          break;
        case 'RELEASED':
          clearStoredDashboardEditLockHolder(data.tabId);
          if (holderTabIdRef.current === data.tabId) holderTabIdRef.current = null;
          break;
        case 'FORCE_RELEASE':
          if (holderTabIdRef.current === tabId.current) {
            clearStoredDashboardEditLockHolder(tabId.current);
            forceReleaseCallbackRef.current?.();
          }
          holderTabIdRef.current = null;
          break;
        case 'QUERY_LOCK_STATE':
          if (holderTabIdRef.current === tabId.current) {
            channel.postMessage({
              type: 'LOCK_STATE_RESPONSE',
              holderTabId: tabId.current
            } satisfies EditLockMessage);
          }
          break;
        case 'LOCK_STATE_RESPONSE':
          if (holderTabIdRef.current === null) {
            setStoredDashboardEditLockHolder(data.holderTabId);
            holderTabIdRef.current = data.holderTabId;
          }
          break;
        default:
          break;
      }
    };

    // Discover any lock already held by another tab.
    channel.postMessage({ type: 'QUERY_LOCK_STATE' } satisfies EditLockMessage);

    window.addEventListener('beforeunload', releaseLock);

    return () => {
      window.removeEventListener('beforeunload', releaseLock);
      releaseLock();
      channel.close();
      channelRef.current = null;
    };
  }, [releaseLock]);

  return {
    requestEdit,
    requestExclusiveAction,
    releaseEdit: releaseLock,
    isConflictDialogOpen,
    handleEndOtherSession,
    handleCancelEdit,
    setForceReleaseCallback
  };
}
